import { AxiosPromise } from 'axios';

import {
  ICarCrossSellResponse,
  ILodgingCrossSellResponse,
} from '@share/common-types';
import { axiosInstance, getHeaders } from '@share/utils';
import { Urls } from '@share/constants';

/** Anchored on the booked lodging (hotel/condo); `type` selects the offered vertical. */
export interface ILodgingCrossSellParams {
  propertyId?: string | number;
  placeId?: string | number;
  checkIn: string;
  checkOut: string;
  countryISO?: string;
  /** Vertical selector (backend `Type`): 'Hotel' | 'Condo' | 'Car' | 'Activity'. */
  type: string;
}

export interface ICarLocationParam {
  id: string | number;
  kind: string;
}

/** Anchored on the booked car locations; `type` selects the offered vertical. */
export interface ICarCrossSellParams {
  carServiceId?: string;
  bookingId?: string;
  includeReturnLocation?: boolean;
  locations: ICarLocationParam[];
  checkIn: string;
  checkOut: string;
  type: string;
}

const buildLodgingQuery = ({ propertyId, placeId, checkIn, checkOut, countryISO, type }: ILodgingCrossSellParams): string => {
  const query: string[] = [`Type=${encodeURIComponent(type)}`];
  if (propertyId != null && String(propertyId) !== '') query.push(`PropertyId=${encodeURIComponent(String(propertyId))}`);
  if (placeId != null && String(placeId) !== '') query.push(`PlaceId=${encodeURIComponent(String(placeId))}`);
  if (checkIn) query.push(`CheckIn=${encodeURIComponent(checkIn)}`);
  if (checkOut) query.push(`CheckOut=${encodeURIComponent(checkOut)}`);
  if (countryISO) query.push(`CountryISO=${encodeURIComponent(countryISO)}`);
  return `?${query.join('&')}`;
};

const buildCarQuery = ({ carServiceId, bookingId, includeReturnLocation, locations, checkIn, checkOut, type }: ICarCrossSellParams): string => {
  const query: string[] = [`Type=${encodeURIComponent(type)}`];
  if (carServiceId) query.push(`CarServiceId=${encodeURIComponent(carServiceId)}`);
  if (bookingId) query.push(`BookingId=${encodeURIComponent(bookingId)}`);
  query.push(`IncludeReturnLocation=${includeReturnLocation === false ? 'false' : 'true'}`);
  // Multi-value collection format (repeated keys).
  locations.forEach((loc) => query.push(`CarLocationIds=${encodeURIComponent(String(loc.id))}`));
  locations.forEach((loc) => query.push(`CarLocationKinds=${encodeURIComponent(loc.kind)}`));
  if (checkIn) query.push(`CheckIn=${encodeURIComponent(checkIn)}`);
  if (checkOut) query.push(`CheckOut=${encodeURIComponent(checkOut)}`);
  return `?${query.join('&')}`;
};

/** Checkout cross-sell recommendations (TRAV23-4194). All endpoints live on the BFF. */
export const CrossSellAPI = {
  getHotelCrossSell(params: ILodgingCrossSellParams): AxiosPromise<ILodgingCrossSellResponse> {
    return axiosInstance.get(`${Urls.HotelCrossSell}${buildLodgingQuery(params)}`, { ...getHeaders() });
  },

  getCondoCrossSell(params: ILodgingCrossSellParams): AxiosPromise<ILodgingCrossSellResponse> {
    return axiosInstance.get(`${Urls.CondoCrossSell}${buildLodgingQuery(params)}`, { ...getHeaders() });
  },

  getCarsCrossSell(params: ICarCrossSellParams): AxiosPromise<ICarCrossSellResponse> {
    return axiosInstance.get(`${Urls.CarsCrossSell}${buildCarQuery(params)}`, { ...getHeaders() });
  },
};
